import { get } from "lodash-es";
import React, { Dispatch, useEffect } from "react";
import { queryCache, useQuery } from "react-query-legacy";
import { useMutation } from "react-query";
import { useParams } from "react-router";
import { BillingEngineService } from "services";
import {
  Organization,
  RateCard,
  RateCardZone,
  Service,
  RateCardZoneVersion,
} from "services/BillingEngineService/interfaces";
import { useOrganizationRateCardContext } from "./OrganizationRateCardProvider";
import { useServicesContext } from "./ServicesContextProvider";

// @TODO: orest - replace with type from react-query
type Status = "loading" | "error" | "success" | "idle" | undefined;

interface Props {
  rateCardId?: string;
  rateCard?: RateCard;
  currentService?: Service;
  enableRateCard: () => void;
  activeZonesCities: string[];
  activeZonesFacilities: string[];
  zoneByCityId?: Record<string, RateCardZone>;
  zoneByFacilityId?: Record<string, RateCardZone>;
  activeZone?: RateCardZone;
  dispatch: Dispatch<RateCardAction>;
  status: Status;
  createRateCardStatus: Status;
  isLoadingZones: boolean;
  hasFinished: boolean;
}

type RateCardAction =
  | {
      type: "CREATE_VERSION";
      version: RateCardZoneVersion;
    }
  | {
      type: "SET_RATE_CARD_ZONES";
      zones: RateCardZone[];
    }
  | {
      type: "UPDATE_RATE_CARD_ZONES";
      zones: RateCardZone[];
    }
  | {
      type: "SET_ACTIVE_ZONE";
      zone: RateCardZone;
    }
  | {
      type: "RESET_HAS_FINISHED";
    };

export interface RateCardState {
  activatedZones: RateCardZone[];
  activeZone?: RateCardZone;
  zonesByCityId: Record<string, RateCardZone>;
  zonesByFacilityId: Record<string, RateCardZone>;
  hasFinished: boolean;
}

const reducer = (state: RateCardState, action: RateCardAction) => {
  switch (action.type) {
    case "CREATE_VERSION": {
      const newState = {
        ...state,
        activeZone: { ...action.version.rateCardZone },
        activatedZones: state.activatedZones.map((zone) => {
          if (action.version.rateCardZoneId === zone.id) {
            return action.version.rateCardZone;
          }
          return zone;
        }),
        zonesByCityId: {
          ...state.zonesByCityId,
          [action.version.rateCardZone.cityId]: action.version.rateCardZone,
        },
        zonesByFacilityId: {
          ...state.zonesByFacilityId,
          [action.version.rateCardZone.facilityId]: action.version.rateCardZone,
        },
        hasFinished: true,
      };
      return newState;
    }

    case "SET_RATE_CARD_ZONES": {
      const activeZones = action.zones.filter((zone) => zone.isActive);

      return {
        activatedZones: activeZones,
        activeZone: activeZones[0],
        zonesByCityId: action.zones.reduce((prev, zone) => {
          prev[zone.cityId] = zone;
          return prev;
        }, {} as Record<string, RateCardZone>),
        zonesByFacilityId: action.zones.reduce((prev, zone) => {
          prev[zone.facilityId] = zone;
          return prev;
        }, {} as Record<string, RateCardZone>),
        hasFinished: true,
      };
    }

    case "UPDATE_RATE_CARD_ZONES": {
      const activeZones = action.zones.filter((zone) => zone.isActive && zone.facilityId);

      return {
        ...state,
        activatedZones: activeZones,
        activeZone: activeZones[0],
        zonesByCityId: action.zones.reduce((prev, zone) => {
          prev[zone.cityId] = zone;
          return prev;
        }, {} as Record<string, RateCardZone>),
        zonesByFacilityId: action.zones.reduce((prev, zone) => {
          prev[zone.facilityId] = zone;
          return prev;
        }, {} as Record<string, RateCardZone>),
        hasFinished: true,
      };
    }

    case "SET_ACTIVE_ZONE": {
      return {
        ...state,
        activeZone: action.zone,
        hasFinished: true,
      };
    }

    case "RESET_HAS_FINISHED": {
      return {
        ...state,
        hasFinished: false,
      };
    }

    default:
      return state;
  }
};

const RateCardContext = React.createContext<Props | undefined>(undefined);

const RateCardProvider: React.FC = ({ children }) => {
  const params = useParams();
  const serviceId = get(params, "service_id");

  const { organizationRateCards, organizationID } = useOrganizationRateCardContext();

  const { services } = useServicesContext();

  /**
   * Use reducer to handle complex state changes
   */
  const [state, dispatch] = React.useReducer(reducer, {
    activatedZones: [],
    activeZone: undefined,
    zonesByCityId: {},
    zonesByFacilityId: {},
    hasFinished: false,
  });

  useEffect(() => {
    dispatch({
      type: "RESET_HAS_FINISHED",
    });
  }, [state.activeZone]);

  const currentService = services?.find((service) => service.id === serviceId);

  const serviceRateCard = organizationRateCards?.rateCards?.find(
    (rateCard) => rateCard.serviceId === currentService?.id
  );

  /**
   * Query to get rate card details
   */
  const { data, status, isFetching } = useQuery(
    serviceRateCard && ["rate-card", serviceRateCard.id],
    BillingEngineService.getRateCardDetails,
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        dispatch({
          type: "SET_RATE_CARD_ZONES",
          zones: data.zones,
        });
      },
    }
  );

  /**
   * Mutation to create rate card
   */
  const { mutate: createRateCard, status: createRateCardStatus } = useMutation(
    BillingEngineService.addRateCardToService,
    {
      onSuccess: (data) => {
        queryCache.setQueryData(["organization", organizationID], (oldData?: Organization) => {
          return oldData
            ? ({
                ...oldData,
                rateCards: oldData.rateCards ? [...oldData.rateCards, data] : oldData.rateCards,
              } as Organization)
            : oldData;
        });
      },
    }
  );

  /**
   * Enable card
   */
  const enableRateCard = () => {
    if (currentService) {
      createRateCard({
        organizationId: organizationID,
        serviceId: currentService.id,
      });
    }
  };

  return (
    <RateCardContext.Provider
      value={{
        rateCard: data as RateCard,
        rateCardId: serviceRateCard?.id,
        currentService,
        status,
        activeZonesCities: state.activatedZones.map((zone) => zone.cityId) || [],
        zoneByCityId: state.zonesByCityId,
        activeZonesFacilities: state.activatedZones.map((zone) => zone.facilityId) || [],
        zoneByFacilityId: state.zonesByFacilityId,
        activeZone: state.activeZone,
        enableRateCard,
        dispatch,
        createRateCardStatus,
        isLoadingZones: isFetching,
        hasFinished: state.hasFinished,
      }}
    >
      {children}
    </RateCardContext.Provider>
  );
};

/**
 * Hook to get access to RateCardProvider
 */
const useRateCardContext = () => {
  const context = React.useContext(RateCardContext);

  if (!context) {
    throw new Error("useOrganizationRateCardContext can be used only inside <RateCardProvider />");
  }

  return context;
};

export { RateCardProvider, useRateCardContext };
